import { tx } from "@/i18n/tx";
import { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtCHF, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Camera, Loader2, Repeat } from "lucide-react";
import Sheet from "@/components/invoices/Sheet";
import InvoiceCard from "@/components/invoices/InvoiceCard";
import DuplicateDialog from "@/components/invoices/DuplicateDialog";
import BatchReviewSheet, { CATEGORIES, TVA_RATES, catLabel } from "@/components/invoices/BatchReviewSheet";
import InvoiceActionSheet from "@/components/invoices/InvoiceActionSheet";
import PaymentChoice, { isPaymentValid, paymentPayload } from "@/components/invoices/PaymentChoice";
import PaymentDialog from "@/components/invoices/PaymentDialog";
import DateField, { localToday as today } from "@/components/invoices/DateField";
import { VERIFY_EVENT } from "@/components/invoices/VerifyBox";
const computeTvaFromRate = (amount, rate) => {
  const a = parseFloat(amount) || 0;
  const r = parseFloat(rate) || 0;
  if (r <= 0) return 0;
  return +(a * r / (100 + r)).toFixed(2);
};

export default function Factures() {
  const [invs, setInvs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [amount, setAmount] = useState("");
  const [due, setDue] = useState("");
  const [category, setCategory] = useState("Autre");
  const [recurring, setRecurring] = useState(false);
  const [tvaRate, setTvaRate] = useState(8.1);
  const [tvaAmount, setTvaAmount] = useState("");
  const [tvaAmountAuto, setTvaAmountAuto] = useState(true); // if true, recomputed on amount/rate change
  const [invNumber, setInvNumber] = useState("");
  const [invDate, setInvDate] = useState(today());
  const [dupPending, setDupPending] = useState(null); // {matches}
  const [action, setAction] = useState(null); // {type, inv}
  const [payment, setPayment] = useState(null);
  const [payHints, setPayHints] = useState({});
  const [askPay, setAskPay] = useState(false);
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [recentSuppliers, setRecentSuppliers] = useState([]);
  const fileRef = useRef(null);
  const suggestTimerRef = useRef(null);

  const loadRecentSuppliers = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices/suppliers-recent?limit=6");
      setRecentSuppliers(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, []);

  const applySupplier = (s) => {
    setSupplier(s.supplier || "");
    setCategory(s.category || "Autre");
    setTvaRate(s.tva_rate || 8.1);
    if (s.last_amount) setAmount(String(s.last_amount));
  };

  const onSupplierChange = (val) => {
    setSupplier(val);
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    if (val.trim().length < 3) return;
    suggestTimerRef.current = setTimeout(async () => {
      setSuggesting(true);
      try {
        const { data } = await api.post("/ai/categorize", { supplier: val }, { timeout: 15000 });
        if (data.category && CATEGORIES.includes(data.category)) setCategory(data.category);
      } catch { /* silent */ }
      finally { setSuggesting(false); }
    }, 900);
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/invoices");
    window.dispatchEvent(new Event(VERIFY_EVENT));
    setInvs(data);
  }, []);
  useEffect(() => { load(); loadRecentSuppliers(); }, [load, loadRecentSuppliers]);

  const filtered = invs.filter((i) => {
    if (filter === "all") return true;
    if (filter === "cancelled") return i.status === "annulee";
    if (i.status === "annulee") return false;
    if (filter === "open") return !i.paid;
    if (filter === "paid") return i.paid;
    return i.status === filter;
  });

  const counts = {
    all: invs.length,
    retard: invs.filter((i) => i.status === "retard").length,
    bientot_du: invs.filter((i) => i.status === "bientot_du").length,
    paid: invs.filter((i) => i.paid && i.status !== "annulee").length,
    cancelled: invs.filter((i) => i.status === "annulee").length,
  };

  // Auto-compute TVA amount when amount or rate changes (if user hasn't manually edited it)
  useEffect(() => {
    if (tvaAmountAuto) {
      setTvaAmount(computeTvaFromRate(amount, tvaRate).toFixed(2));
    }
  }, [amount, tvaRate, tvaAmountAuto]);

  const onTvaAmountChange = (val) => {
    setTvaAmount(val.replace(",", "."));
    setTvaAmountAuto(false);
  };

  const resetForm = () => {
    setSupplier(""); setAmount(""); setDue(""); setCategory("Autre");
    setRecurring(false); setTvaRate(8.1); setTvaAmount(""); setTvaAmountAuto(true);
    setInvNumber(""); setInvDate(today()); setPayment(null); setPayHints({});
  };

  const saveInvoice = async (payload) => {
    setLoading(true);
    try {
      await api.post("/invoices", payload);
      toast.success(payload.recurring ? tx("ui_factures.k266e77") : tx("ui_factures.k954f76"));
      setOpen(false);
      resetForm();
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.existing) setDupPending({ matches: [{ ...d.existing, date: d.existing.invoice_date }] });
      else toast.error(formatApiErrorDetail(d));
    } finally { setLoading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!supplier || !amount) { toast.error(tx("ui_factures.k5135a2")); return; }
    if (!invDate) { toast.error(tx("r3.date_required")); return; }
    if (!isPaymentValid(payment)) { toast.error(tx("ui_factures.kb1c8df")); return; }
    const dueDate = payment.method === "a_payer" ? payment.date : (due || invDate || today());
    setLoading(true);
    try {
      const payload = {
        supplier,
        amount: parseFloat(amount),
        due_date: dueDate,
        category,
        notes: "",
        recurring,
        tva_rate: parseFloat(tvaRate) || 0,
        tva_amount: tvaAmount === "" ? null : parseFloat(tvaAmount),
        invoice_number: invNumber.trim(),
        invoice_date: invDate,
        ...paymentPayload(payment),
      };
      const { data } = await api.post("/invoices/check-duplicates", { items: [{ supplier, invoice_number: payload.invoice_number, amount: payload.amount, invoice_date: invDate, due_date: dueDate }] });
      const matches = data.matches?.[0] || [];
      setLoading(false);
      if (matches.length) { setDupPending({ matches }); return; }
      await saveInvoice(payload);
    } catch (err) {
      setLoading(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/invoices/${id}/pay`, { paid_date: today() });
      toast.success(tx("ui_factures.kc5121b"));
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const onCardAction = (type, inv) => (type === "pay" ? markPaid(inv.id) : setAction({ type, inv }));

  const onScanFile = async (e) => {
    const filesSelected = Array.from(e.target.files || []);
    if (filesSelected.length === 0) return;
    // Detect if user uploaded any PDF (multi-invoice bulk PDF or one-per-invoice PDFs)
    const hasPdf = filesSelected.some((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    for (const f of filesSelected) {
      if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name} trop volumineux (max 12 Mo)`); return; }
    }
    setScanning(true);
    try {
      // Force batch mode for PDFs (a single PDF may contain many invoices)
      if (filesSelected.length === 1 && !hasPdf) {
        const form = new FormData();
        form.append("file", filesSelected[0]);
        const { data } = await api.post("/invoices/scan", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        setSupplier(data.supplier || "");
        setAmount(data.amount ? String(data.amount) : "");
        setDue(data.due_date || "");
        setCategory(CATEGORIES.includes(data.category) ? data.category : "Autre");
        const rate = TVA_RATES.includes(data.tva_rate) ? data.tva_rate : 8.1;
        setTvaRate(rate);
        setInvNumber(data.invoice_number || "");
        setInvDate(data.invoice_date || "");
        setPayment(null);
        setPayHints({ cashDate: data.paid_date || data.invoice_date || "", dueDate: data.due_date || "" });
        if (data.tva_amount != null && data.tva_amount > 0) {
          setTvaAmount(String(data.tva_amount));
          setTvaAmountAuto(false);
        } else {
          setTvaAmountAuto(true);
        }
        setAskPay(true);
      } else {
        const form = new FormData();
        filesSelected.forEach((f) => form.append("files", f));
        const { data } = await api.post("/invoices/scan-batch", form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
        setBatch(data.results || []);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || tx("r3.analysis_failed"));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };


  const duplicate = async (id) => {
    try {
      await api.post(`/invoices/${id}/duplicate`);
      toast.success(tx("ui_factures.kfc46c3"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const Label = ({ children }) => <label className="text-sm font-medium text-text-secondary block mb-1">{children}</label>;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-3xl">{tx("ui_factures.kf510a8")}</h1>
          <p className="text-text-secondary text-sm">{tx("ui_factures.k9690d0")}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="scan-invoice-button" onClick={() => fileRef.current?.click()} disabled={scanning}
            className="h-12 min-w-12 px-3.5 rounded-2xl bg-secondary-c text-white flex items-center justify-center gap-2 shadow-sm disabled:opacity-50 font-semibold text-sm"
            title={tx("ui_factures.kff2851")}>
            {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            <span className="hidden sm:inline">{tx("ui_factures.k948505")}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.pdf" multiple hidden onChange={onScanFile} data-testid="scan-invoice-file" />
          <button data-testid="add-invoice-button" onClick={() => setOpen(true)} className="h-12 min-w-12 px-3.5 rounded-2xl bg-primary-c text-white flex items-center justify-center gap-2 font-semibold text-sm">
            <Plus className="w-6 h-6" /><span className="hidden sm:inline">{tx("ui_factures.k18d925")}</span>
          </button>
        </div>
      </div>

      {scanning && (
        <div className="card-c flex items-center gap-3 border-2 border-secondary-c bg-[#FFF8EB]" data-testid="scan-loading-banner">
          <Loader2 className="w-5 h-5 text-secondary-c animate-spin shrink-0" />
          <p className="text-sm text-[#7A5C10]">{tx("ui_factures.k408efb")}</p>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {[
          { k: "all", l: `Toutes (${counts.all})` },
          { k: "retard", l: `Retard (${counts.retard})` },
          { k: "bientot_du", l: tx("tpl.f_soon", { n: counts.bientot_du }) },
          { k: "open", l: tx("ui_factures.kf5b76c") },
          { k: "paid", l: tx("tpl.f_paid", { n: counts.paid }) },
          { k: "cancelled", l: tx("tpl.f_cancelled", { n: counts.cancelled }) },
        ].map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)}
            className={`shrink-0 h-9 px-3.5 rounded-full text-sm font-semibold border ${filter === f.k ? "bg-primary-c text-white border-transparent" : "bg-surface text-text-secondary border-c"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-text-secondary text-sm text-center py-12">{tx("ui_factures.k622973")}</p>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {filtered.map((inv) => (
            <InvoiceCard key={inv.id} inv={inv} onAction={onCardAction} onDuplicate={duplicate} />
          ))}
        </ul>
      )}

      {open && (
        <Sheet
          title={tx("ui_factures.keebed3")}
          testId="invoice-modal"
          onClose={() => setOpen(false)}
          footer={<button form="invoice-form" data-testid="invoice-submit-button" disabled={loading} className="btn-primary w-full">{loading ? "Ajout…" : tx("ui_factures.ked87de")}</button>}
        >
          <form id="invoice-form" onSubmit={onSubmit} className="space-y-4">
            <p className="text-xs text-text-secondary">{tx("ui_factures.ke44722")} <Camera className="inline w-3.5 h-3.5 text-secondary-c" /> {tx("ui_factures.k930fd4")}</p>
            <div>
              <Label>{tx("ui_factures.k73d163")}</Label>
              <input data-testid="invoice-supplier-input" required value={supplier} onChange={(e) => onSupplierChange(e.target.value)} className="input-c" placeholder={tx("ui_factures.k542421")} />
              {recentSuppliers.length > 0 && (
                <div className="mt-2" data-testid="invoice-recent-suppliers">
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1.5">{tx("ui_factures.k9673aa")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {recentSuppliers.map((s, idx) => (
                      <button key={s.supplier} type="button" onClick={() => applySupplier(s)} data-testid={`invoice-supplier-chip-${idx}`}
                        className="text-xs h-8 px-3 rounded-full bg-surface-muted border border-c hover:bg-primary-c/10 hover:border-primary-c/40 transition-colors truncate max-w-[180px]">
                        {s.supplier}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
              <div><Label>{tx("r3.inv_no")}</Label><input data-testid="invoice-number-input" value={invNumber} onChange={(e) => setInvNumber(e.target.value)} className="input-c" placeholder={tx("ui_factures.k7cf4c0")} /></div>
              <div><Label>{tx("ui_factures.kfdac99")}</Label><DateField value={invDate} onChange={setInvDate} testId="invoice-date-input" /></div>
              <div><Label>{tx("ui_factures.k5b2858")}</Label><input data-testid="invoice-amount-input" required inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(",", "."))} className="input-c" placeholder="0.00" /></div>
            </div>

            <div className="p-3 bg-surface-muted rounded-xl space-y-2" data-testid="invoice-paid-block">
              <p className="text-sm font-semibold">{tx("ui_factures.k1b91ea")}</p>
              <PaymentChoice value={payment} onChange={setPayment} hints={{ cashDate: payHints.cashDate || invDate || today(), dueDate: payHints.dueDate || due }} testPrefix="invoice-payment" />
            </div>

            <div className="p-3 bg-surface-muted rounded-xl space-y-2" data-testid="invoice-tva-block">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-secondary-c">{tx("ui_factures.k59978d")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-text-secondary block mb-1">{tx("ui_factures.kd8f3a0")}</label>
                  <select data-testid="invoice-tva-rate-select" value={tvaRate} onChange={(e) => { setTvaRate(parseFloat(e.target.value)); setTvaAmountAuto(true); }} className="input-c text-sm">
                    {TVA_RATES.map((r) => <option key={r} value={r}>{r === 0 ? tx("ui_factures.ke1a82c") : `${r} %`}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-secondary block mb-1">{tx("ui_factures.k79eff3")}</label>
                  <input data-testid="invoice-tva-amount-input" inputMode="decimal" value={tvaAmount} onChange={(e) => onTvaAmountChange(e.target.value)} className="input-c text-sm" placeholder="0.00" />
                </div>
              </div>
              <p className="text-[10px] text-text-secondary">
                {tvaAmountAuto ? tx("ui_factures.k90bd6f") : tx("ui_factures.k9e95dc")}
                {" "}HT = <strong className="text-[var(--text)]">{fmtCHF((parseFloat(amount) || 0) - (parseFloat(tvaAmount) || 0))}</strong>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-text-secondary mb-1 flex items-center gap-2">
                {tx("ui_factures.k8c6b7f")}
                {suggesting && <span className="text-[10px] text-secondary-c flex items-center gap-1" data-testid="category-suggesting"><Loader2 className="w-3 h-3 animate-spin" />{tx("ui_factures.kba7510")}</span>}
              </label>
              <select data-testid="invoice-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className="input-c">
                {CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 p-3 bg-surface-muted rounded-xl cursor-pointer" data-testid="invoice-recurring-toggle">
              <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="w-5 h-5 accent-[var(--primary)] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold flex items-center gap-2"><Repeat className="w-3.5 h-3.5 text-primary-c" />{tx("ui_factures.kfe8296")}</p>
                <p className="text-[11px] text-text-secondary mt-0.5">{tx("ui_factures.ka08dc4")}</p>
              </div>
            </label>
          </form>
        </Sheet>
      )}

      {askPay && (
        <PaymentDialog
          hints={{ cashDate: payHints.cashDate || today(), dueDate: payHints.dueDate }}
          onConfirm={(p) => { setPayment(p); setAskPay(false); setOpen(true); toast.success(tx("ui_factures.k1b6518")); }}
          onCancel={() => { setAskPay(false); resetForm(); }}
        />
      )}

      {batch && <BatchReviewSheet results={batch} onClose={() => setBatch(null)} onSaved={async () => { setBatch(null); await load(); }} />}

      <DuplicateDialog
        open={dupPending}
        title={tx("ui_factures.k97e815")}
        description={dupPending && dupPending.matches.slice(0, 2).map((m) => (
          <p key={m.id}>{m.supplier}{m.invoice_number ? ` · n° ${m.invoice_number}` : ""} · {fmtCHF(m.amount)} · {fmtDate(m.date)}</p>
        ))}
        confirmLabel={null}
        cancelLabel={tx("ui_factures.k7d2062")}
        onCancel={() => setDupPending(null)}
        onConfirm={() => setDupPending(null)}
      />
      {action && <InvoiceActionSheet action={action} onClose={() => setAction(null)} onDone={async () => { setAction(null); await load(); }} />}
    </div>
  );
}
